/** Token-gated logo media route (no Firebase Auth — token is the credential). */
export const LOGO_MEDIA_API_PATH = '/api/storage/logo-file';

export function buildLogoMediaUrl(baseUrl: string, storagePath: string, token: string): string {
  const origin = baseUrl.replace(/\/$/, '');
  return `${origin}${LOGO_MEDIA_API_PATH}?path=${encodeURIComponent(storagePath)}&token=${encodeURIComponent(token)}`;
}

/**
 * R-131-ATTACH — token-gated bug-attachment media route. Like the logo route,
 * the download token IS the credential (no Firebase Auth), which is what lets
 * an admin open a reporter's screenshot from the triage panel even though the
 * object lives under the reporter's `users/{uid}/…` prefix.
 */
export const ATTACHMENT_MEDIA_API_PATH = '/api/storage/attachment-file';

export function buildAttachmentMediaUrl(baseUrl: string, storagePath: string, token: string): string {
  const origin = baseUrl.replace(/\/$/, '');
  return `${origin}${ATTACHMENT_MEDIA_API_PATH}?path=${encodeURIComponent(storagePath)}&token=${encodeURIComponent(token)}`;
}

export function isHostedLogoMediaUrl(logoUrl: string): boolean {
  try {
    const u = new URL(logoUrl, 'https://placeholder.local');
    return u.pathname === LOGO_MEDIA_API_PATH || u.pathname.endsWith(`${LOGO_MEDIA_API_PATH}`);
  } catch {
    return logoUrl.includes(LOGO_MEDIA_API_PATH);
  }
}

/**
 * Normalize a hosted logo-media URL to ORIGIN-RELATIVE form so it always
 * resolves against the current app origin. Fixes logo URLs generated server-side
 * with a baked-in `http://localhost:3000` (or run.app) origin — when
 * PUBLIC_APP_URL was unset / on an internal request — which render as
 * mixed-content (blocked) or connection-refused in prod. The media route is
 * same-origin, so a relative URL is always correct. Non-hosted URLs (Firebase
 * Storage, gs://, data:, external CDNs) are returned unchanged.
 */
export function toRelativeLogoMediaUrl(logoUrl: string | undefined | null): string {
  if (!logoUrl) return '';
  if (!isHostedLogoMediaUrl(logoUrl)) return logoUrl;
  try {
    const u = new URL(logoUrl, 'https://placeholder.local');
    return `${u.pathname}${u.search}`;
  } catch {
    return logoUrl;
  }
}

/** Extract `logos/…` object path from hosted, Firebase, or gs:// URLs. */
export function parseLogoStoragePathFromUrl(logoUrl: string): string | null {
  if (isHostedLogoMediaUrl(logoUrl)) {
    try {
      const u = new URL(logoUrl, 'https://placeholder.local');
      const path = u.searchParams.get('path');
      if (!path || path.includes('..')) return null;
      return path;
    } catch {
      return null;
    }
  }
  const fb = /firebasestorage\.googleapis\.com\/v0\/b\/[^/]+\/o\/([^?]+)/.exec(logoUrl);
  if (fb) {
    try {
      return decodeURIComponent(fb[1].replace(/\+/g, ' '));
    } catch {
      return null;
    }
  }
  if (logoUrl.startsWith('gs://')) {
    const m = /^gs:\/\/[^/]+\/(.+)$/.exec(logoUrl);
    return m?.[1] ?? null;
  }
  return null;
}

/** GCS may store multiple comma-separated download tokens (Firebase convention). */
export function logoDownloadTokenMatches(
  storedTokens: string | undefined,
  requestedToken: string,
): boolean {
  if (!storedTokens || !requestedToken) return false;
  return storedTokens.split(',').some((t) => t.trim() === requestedToken);
}
